// Retken varmuuskopio IndexedDB:hen. Vanha localStorage säilyy edelleen
// ensisijaisena synkronisena tallennuksena ja migraation varalähteenä.
// Tämä on yhteensopiva välivaihe, EI transaktionaalinen pistejournalointi.
// Kirjoitukset jonotetaan, jotta vanha snapshot ei korvaa uudempaa.

const DB_NAME = "tilehunting-local";
const STORE = "snapshots";
const KEY = "recorder";
let pending = Promise.resolve();

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB ei ole käytettävissä"));
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function putSnapshot(snapshot) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(snapshot, KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function readSnapshot() {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get(KEY);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Irrotettu kopio snapshotista. Vanhat Android-WebView-versiot eivät tunne
 * structuredClonea, joten varana on JSON-kopio (snapshot on pelkkää JSONia).
 */
export function cloneSnapshot(snapshot) {
  if (typeof structuredClone === "function") return structuredClone(snapshot);
  return JSON.parse(JSON.stringify(snapshot));
}

export function queueRecorderMirror(snapshot) {
  // Varmistus ei saa koskaan kaataa tallennusta: kopiointivirhe palautetaan
  // hylättynä lupauksena, ja localStorage jää varalähteeksi.
  let copy;
  try {
    copy = cloneSnapshot(snapshot);
  } catch (error) {
    pending = Promise.reject(error);
    pending.catch(() => {});
    return pending;
  }
  pending = pending.catch(() => {}).then(() => putSnapshot(copy));
  // Estetään käsittelemätön Promise rejection, mutta virhe on kysyttävissä
  // erillisestä await-kutsusta. LocalStorage säilyy varalähteenä.
  pending.catch(() => {});
  return pending;
}

export async function waitForRecorderMirror() {
  return pending;
}

/** Kutsutaan ENNEN käyttöliittymän renderöintiä ja Recorderin rakentamista. */
export async function recoverRecorderSnapshot() {
  if (typeof localStorage === "undefined") return { source: "none" };
  let local = null;
  try { local = JSON.parse(localStorage.getItem("tilehunt:recorder") || "null"); } catch {}
  let mirror = null;
  try { mirror = await readSnapshot(); } catch (error) {
    return { source: local ? "localStorage" : "none", warning: String(error?.message ?? error) };
  }
  const localTime = Number(local?.savedAt) || 0;
  const mirrorTime = Number(mirror?.savedAt) || 0;
  if (mirror && (!local || mirrorTime > localTime)) {
    try {
      localStorage.setItem("tilehunt:recorder", JSON.stringify(mirror));
      return { source: "indexedDB", restored: true };
    } catch (error) {
      return { source: "indexedDB", restored: false, warning: String(error?.message ?? error) };
    }
  }
  if (local && !mirror) queueRecorderMirror(local);
  return { source: local ? "localStorage" : "none" };
}
